<template>

  <v-container class="py-4 ma-0 location-status-manager" :class="locationStatus">

    <!-------------------->
    <!-- Requires Setup -->
    <!-------------------->
    <v-row v-if="locationStatus === 'REQUIRES_SETUP'">
      <v-col cols="12" xs="12" sm="12" md="6" lg="7">
        <div class="align-center justify-center">
          <div class="d-flex align-center justify-center">
            <v-icon style="font-size:30px;" class="mr-2" color="#FF5D67">mdi-alert-circle-outline</v-icon>
            <span class="action-title">Location Missing Info</span>
          </div>
          <div class="action-info d-flex align-center justify-center mt-4 px-6">
            <div align="center" style="color:#FF5D67">Provide all required information to allow {{ appName }} shipments to this location.</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="6" lg="5">
        <div class="d-flex align-center justify-center">
          <v-btn text icon color="primary" @click="showMissingInfo = !showMissingInfo">
            <span class="mr-1">Missing info</span>
            <v-icon v-if="!showMissingInfo">mdi-chevron-down</v-icon>
            <v-icon v-else>mdi-chevron-up</v-icon>
          </v-btn>
        </div>
        <transition name="fade" appear>
          <div class="mb-4 mt-0 pl-3 more-info-text" v-if="showMissingInfo">
            <div v-for="(item, index) in missingItems" :key="`missing-info-${index}`">
              - {{ item.label }}
            </div>
          </div>
        </transition>
      </v-col>
    </v-row>

    <!----------------------->
    <!-- Requires Approval -->
    <!----------------------->
    <template v-if="locationStatus === 'REQUIRES_APPROVAL'">
      <v-row>
        <v-col cols="12" xs="12" sm="12" :md="(isMyOrgOrSysAdmin) ? '7' : '12'" :lg="(isMyOrgOrSysAdmin) ? '7' : '12'">
          <div class="align-center justify-center">
            <div class="d-flex align-center justify-center">
              <v-icon style="font-size:30px;" class="mr-2" color="#FF9D29">mdi-check-bold</v-icon>
              <span class="action-title">Ready for Approval Request</span>
            </div>
            <div class="action-info d-flex align-center justify-center mt-4">
              <div align="center" v-if="isMyOrgOrSysAdmin">
                <div>License related info has changed.</div>
                <div>Submit the changes to {{ appName }} for approval when ready.</div>
              </div>
              <div align="center" v-else>
                <div>The customer's records are being updated.</div>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="5" lg="5" class="d-flex align-center justify-center" v-if="isMyOrgOrSysAdmin">
          <v-progress-circular indeterminate :size="30" color="primary" v-if="isProcessing" />
          <v-btn
              v-else
              color="primary"
              @click="submitForApproval">
            Submit for Approval
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="mt-3" v-if="isMyOrgOrSysAdmin">
        <v-col cols="12" class="pt-0">
          <div class="d-flex align-center justify-center">
            <v-btn text icon color="primary" @click="showPendingChanges = !showPendingChanges">
              <span class="mr-1">Pending changes</span>
              <v-icon v-if="!showPendingChanges">mdi-chevron-down</v-icon>
              <v-icon v-else>mdi-chevron-up</v-icon>
            </v-btn>
          </div>
          <transition name="fade" appear>
            <div class="mt-0 pl-6 more-info-text" v-if="showPendingChanges">
              <div class="pt-2 pb-2" v-for="(item, index) in Object.keys(pendingChanges)" :key="`pending-change-${index}`">
                <div class="changed-field-label mb-1">
                  {{ infoLabelMap[item] }}
                </div>
                <div v-if="item.endsWith('_issue_date') || item.endsWith('_expiration') || item.endsWith('_amended_date')">
                  <span class="changed-field-value-from">{{ (pendingChanges[item].changed_from) ? formatDate(pendingChanges[item].changed_from) : 'n/a' }}</span>
                  <span class="changed-field-separator">⭢</span>
                  <span class="changed-field-value-to">{{ (pendingChanges[item].changed_to) ? formatDate(pendingChanges[item].changed_to) : 'n/a' }}</span>
                </div>
                <div v-else-if="item !== 'init_message'">
                  <span class="changed-field-value-from">{{ (pendingChanges[item].changed_from ) ? pendingChanges[item].changed_from : 'n/a' }}</span>
                  <span class="changed-field-separator">⭢</span>
                  <span class="changed-field-value-to">{{ (pendingChanges[item].changed_to) ? pendingChanges[item].changed_to : 'n/a' }}</span>
                </div>
              </div>
            </div>
          </transition>
        </v-col>
      </v-row>
    </template>

    <!---------------------->
    <!-- Pending Approval -->
    <!---------------------->
    <template v-if="locationStatus === 'PENDING_APPROVAL'">
      <v-row>
        <v-col cols="12" xs="12" sm="12" :md="(isMyOrgOrSysAdmin) ? '7' : '12'" :lg="(isMyOrgOrSysAdmin) ? '7' : '12'">
          <div class="align-center justify-center">
            <div class="d-flex align-center justify-center">
              <v-icon style="font-size:30px;" class="mr-2" color="#FFD600">mdi-clock-check-outline</v-icon>
              <span class="action-title">Pending Approval</span>
            </div>
            <div class="action-info d-flex align-center justify-center mt-4">
              <div align="center" v-if="isMyOrg">
                <div>Location changes have been submitted.</div>
                <div>A {{ appName }} administrator is reviewing the information for approval.</div>
              </div>
              <div align="center" v-else-if="uiType === 'sys-admin'">
                <div>Location changes have been submitted.</div>
                <div>The location is ready for review and approval.</div>
              </div>
              <div align="center" v-else>
                <div>The customer's records are updating.</div>
                <div>This process typically takes within minutes. Check back soon.</div>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="5" lg="5" class="d-flex align-center justify-center" v-if="isMyOrgOrSysAdmin">
          <v-progress-circular indeterminate :size="30" color="primary" v-if="isProcessing" />
          <template v-else>
            <div class="d-flex flex-column align-center">
              <div class="mb-4 timestamp-info">
                Submitted: {{ formatDate(location.last_submitted_at) }}
              </div>
              <v-btn
                  v-if="isMyOrg"
                  color="grey darken-3"
                  @click="undoSubmit">
                Make More Changes
              </v-btn>
              <v-btn
                  v-if="uiType === 'sys-admin'"
                  large
                  color="primary"
                  @click="approve">
                Approve Location
              </v-btn>
            </div>
          </template>
        </v-col>
      </v-row>
      <v-row class="mt-3" v-if="isMyOrgOrSysAdmin">
        <v-col cols="12" class="pt-0">
          <div class="d-flex align-center justify-center">
            <v-btn text icon color="primary" @click="showPendingChanges = !showPendingChanges">
              <span class="mr-1">Pending changes</span>
              <v-icon v-if="!showPendingChanges">mdi-chevron-down</v-icon>
              <v-icon v-else>mdi-chevron-up</v-icon>
            </v-btn>
          </div>
          <transition name="fade" appear>
            <div class="mt-0 pl-6 more-info-text" v-if="showPendingChanges">
              <div class="pt-2 pb-2" v-for="(item, index) in Object.keys(pendingChanges)" :key="`pending-change-${index}`">
                <div class="changed-field-label mb-1">
                  {{ infoLabelMap[item] }}
                </div>
                <div v-if="item.endsWith('_issue_date') || item.endsWith('_expiration') || item.endsWith('_amended_date')">
                  <span class="changed-field-value-from">{{ (pendingChanges[item].changed_from) ? formatDate(pendingChanges[item].changed_from) : 'n/a' }}</span>
                  <span class="changed-field-separator">⭢</span>
                  <span class="changed-field-value-to">{{ (pendingChanges[item].changed_to) ? formatDate(pendingChanges[item].changed_to) : 'n/a' }}</span>
                </div>
                <div v-else-if="item !== 'init_message'">
                  <span class="changed-field-value-from">{{ (pendingChanges[item].changed_from) ? pendingChanges[item].changed_from : 'n/a' }}</span>
                  <span class="changed-field-separator">⭢</span>
                  <span class="changed-field-value-to">{{ (pendingChanges[item].changed_to) ? pendingChanges[item].changed_to : 'n/a' }}</span>
                </div>
              </div>
            </div>
          </transition>
        </v-col>
      </v-row>
    </template>

    <!-------------->
    <!-- Approved -->
    <!-------------->
    <template v-if="locationStatus === 'APPROVED'">
      <v-row>
        <v-col cols="12" class="d-flex align-center justify-center" :class="hasRecentChanges ? 'py-4' : 'py-8'">
          <div class="action-info">
            <div class="timestamp-info">Last approved: <span class="ml-1">{{ lastApprovedAt }}</span></div>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="hasRecentChanges">
        <v-col cols="12" class="pt-0 pb-1">
          <div class="d-flex align-center justify-center pb-2">
            <v-btn text icon color="primary" @click="showRecentChanges = !showRecentChanges">
              <span class="mr-1">Most recent changes</span>
              <v-icon v-if="!showRecentChanges">mdi-chevron-down</v-icon>
              <v-icon v-else>mdi-chevron-up</v-icon>
            </v-btn>
          </div>
          <transition name="fade" appear>
            <div class="mt-0 pl-6 more-info-text" v-if="showRecentChanges">
                <div class="pt-2 pb-2" v-for="(item, index) in Object.keys(recentChanges)" :key="`recent-change-${index}`">
                  <div class="changed-field-label mb-1">
                    {{ infoLabelMap[item] }}
                  </div>
                  <div v-if="item.endsWith('_issue_date') || item.endsWith('_expiration') || item.endsWith('_amended_date')">
                    <span class="changed-field-value-from">{{ (recentChanges[item].changed_from) ? formatDate(recentChanges[item].changed_from) : 'n/a' }}</span>
                    <span class="changed-field-separator">⭢</span>
                    <span class="changed-field-value-to">{{ (recentChanges[item].changed_to) ? formatDate(recentChanges[item].changed_to) : 'n/a' }}</span>
                  </div>
                  <div v-else>
                    <span class="changed-field-value-from">{{ (recentChanges[item].changed_from) ? recentChanges[item].changed_from : 'n/a' }}</span>
                    <span class="changed-field-separator">⭢</span>
                    <span class="changed-field-value-to">{{ (recentChanges[item].changed_to) ? recentChanges[item].changed_to : 'n/a' }}</span>
                  </div>
                </div>
              </div>
          </transition>
        </v-col>
      </v-row>
    </template>

  </v-container>

</template>

<script>
export default {
  name: 'LocationStatusManager',

  props: {
    location: {
      type: Object,
      required: true
    },
    uiType: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      showMissingInfo: false,
      showRecentChanges: false,
      showPendingChanges: false,
      isProcessing: false,
      processingError: null,
      infoLabelMap: {
        init_message: 'New license registration',
        street_1: 'Ship-to address street',
        street_2: 'Ship-to address street 2',
        city: 'Ship-to address city',
        state: 'Ship-to address state',
        zip: 'Ship-to address zip',
        license_for_shipping_address: 'License for ship-to address',
        establishment_license_number: 'Establishment license number',
        establishment_license_category: 'Establishment license category',
        establishment_license_issue_date: 'Establishment license issue date',
        establishment_license_expiration: 'Establishment license expiration',
        establishment_license_amended_date: 'Establishment license amended date',
        drc_number: 'DRC number',
        drc_category: 'DRC category',
        drc_issue_date: 'DRC issue date',
        drc_expiration: 'DRC expiration',
        drc_amended_date: 'DRC amended date'
      },
      requiredInfo: [
        { prop: 'street_1', label: 'Ship-to address street' },
        { prop: 'city', label: 'Ship-to address city' },
        { prop: 'state', label: 'Ship-to address state' },
        { prop: 'zip', label: 'Ship-to address zip' },
        { prop: 'establishment_license_number', label: 'Valid facility license' }
      ]
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    locationType () {
      return (this.location.model === 'Shipper') ? 'shipper' : 'receiver'
    },
    locationStatus () {
      return this.location[`${this.locationType}_status`]
    },
    isMyOrg () {
      return this.uiType && this.uiType === this.locationType
    },
    isMyOrgOrSysAdmin () {
      return this.isMyOrg || this.uiType === 'sys-admin'
    },
    missingItems () {
      return this.requiredInfo.filter(item => !this.location[item.prop])
    },
    pendingChanges () {
      if (this.location.pending_changes && !this.$root.utils.isEmpty(this.location.pending_changes)) {
        return this.location.pending_changes
      } else {
        return { init_message: true }
      }
    },
    lastApprovedLicense () {
      if (!this.location.approved_licenses) return null
      else {
        const sorted = this.$root.utils.orderBy(this.location.approved_licenses, ['approved_at'], ['desc'])
        return sorted[0]
      }
    },
    lastApprovedAt () {
      return this.lastApprovedLicense ? this.formatDate(this.lastApprovedLicense.approved_at) : ''
    },
    hasRecentChanges () {
      return !this.$root.utils.isEmpty(this.recentChanges)
    },
    recentChanges () {
      return (this.lastApprovedLicense) ? this.lastApprovedLicense.changed_info : {}
    }
  },

  methods: {
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMMM D, YYYY') : ''
    },

    async submitForApproval () {
      try {
        this.isProcessing = true

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `${this.locationType}/${this.location.id}/request-approval`
        })

        // DEVING - success/failure examples
        // await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve({ success: true })
        //   }, 4000)
        // })

        // Emit event
        this.$emit('updated', { action: 'request-approval' })
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    },

    async undoSubmit () {
      try {
        this.isProcessing = true

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `${this.locationType}/${this.location.id}/retract-approval-request`
        })

        // Emit event
        this.$emit('updated', { action: 'retract-approval-request' })
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    },

    async approve () {
      if (this.uiType !== 'sys-admin') return false

      try {
        this.isProcessing = true

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `${this.locationType}/${this.location.id}/approve`
        })

        // Emit event
        this.$emit('updated', { action: 'approved' })
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
  .location-status-manager.REQUIRES_SETUP {
    color: #FF5D67;
    border: 1px solid #FF5D67;
    border-radius: 4px;
  }

  .location-status-manager.REQUIRES_APPROVAL {
    color: #FF9D29;
    border: 1px solid #FF9D29;
    border-radius: 4px;
  }

  .location-status-manager.PENDING_APPROVAL {
    color: #FFD600;
    border: 1px solid #FFD600;
    border-radius: 4px;
  }

  .location-status-manager.APPROVED {
    color: #525252;
    border: 1px solid #525252;
    border-radius: 4px;
  }

  .location-status-manager .action-title {
    font-size: 1.14rem;
    font-weight: 500;
  }

  .location-status-manager .action-info {
    font-size: 1.0rem;
    color: #9c9c9c;
  }

  .location-status-manager .more-info-text {
    color: #ffffff;
  }

  .location-status-manager .timestamp-info {
    font-size: 0.94rem;
    color: #727272;
  }

  .location-status-manager .changed-field-label {
    color: #e9e9e9;
    font-weight: 500;
  }

  .location-status-manager .changed-field-value-from {
    color: #EF5350;
  }

  .location-status-manager .changed-field-value-to {
    color: #43A047;
  }

  .location-status-manager .changed-field-separator {
    color: #9c9c9c;
    margin-left: 8px;
    margin-right: 8px;
  }
</style>
